import AxiosInstance from '@/api/axios';
import {
  defaultPagination,
  PaginationInterface,
} from '@/components/Base/Pagination';
import { toast } from '@/components/Base/Toast/ToastManager';
import { checkClient } from '@/utils/checkAccess';
import { getClientId } from '@/utils/helper';
import { FileFilterType, FileResponseType } from '@/utils/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

interface DeleteTrainingFileParams {
  id: string;
  intent: string;
  branch?: string;
  region?: string;
}

interface TrainingStateType {
  data: {
    data: FileResponseType[] | null | undefined;
    pagination: PaginationInterface;
  };
  loading: boolean;
  error: string | null;
}

interface SaveFileParams {
  textContent: string;
  selectedFile: {
    original_filename?: string;
    content_type?: string;
  };
  intent?: string;
  selectedRegion?: string;
  selectedBranch?: string;
}

export const fetchTrainingFiles = createAsyncThunk(
  'fetchTraining/fetchData',
  async (
    {
      branch_id,
      currentPage,
      perpage,
      search,
      sort,
      order,
      region_id,
    }: FileFilterType,
    thunkAPI
  ) => {
    try {
      if (!region_id && !branch_id) {
        return null;
      }
      const response = await AxiosInstance.get(`/files`, {
        params: {
          organization_id: checkClient(),
          region_id: region_id ? region_id : '',
          branch_id: branch_id ? branch_id : '',
          page: currentPage,
          perpage: perpage,
          sort,
          order,
          search,
        },
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }
);

export const deleteTrainingFile = createAsyncThunk(
  'deleteTrainingFile',
  async ({ id, intent, branch, region }: DeleteTrainingFileParams) => {
    try {
      const response = await AxiosInstance.delete(`/files/${id}`, {
        params: {
          organization_id: getClientId(),
          intent,
          branch,
          region,
        },
      });
      if (response.data.success) {
        toast.show({
          title: 'Changes saved successfully.',
          content: 'File deleted',
          duration: 2000,
          type: 'primary',
        });
        return response.data.data;
      }
    } catch (error) {
      toast.show({
        title: 'Error',
        content: 'Failed to delete file',
        duration: 2000,
        type: 'warning',
      });
      return null;
    }
  }
);

export const saveTrainingFile = createAsyncThunk(
  'trainingFiles/saveFile',
  async (
    {
      textContent,
      selectedFile,
      intent,
      selectedRegion,
      selectedBranch,
    }: SaveFileParams,
    thunkAPI
  ) => {
    try {
      if (!textContent) {
        throw new Error('Required data is missing');
      }
      const formData = new FormData();
      const textBlob = new Blob([textContent], {
        type: selectedFile.content_type || 'text/plain',
      });
      formData.append(
        'files',
        textBlob,
        selectedFile.original_filename || 'edited_file.txt'
      );

      const client = getClientId();

      const queryParams = new URLSearchParams({
        organization_id: client as string,
        intent: intent || 'Default',
        clearOldDocuments: 'false',
      });

      if (selectedRegion) {
        queryParams.append('region_id', selectedRegion);
      } else if (selectedBranch) {
        queryParams.append('branch', selectedBranch);
      }

      const response = await AxiosInstance.post(
        `/files?${queryParams.toString()}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.message === 'success') {
        toast.show({
          title: 'Changes saved successfully',
          content: 'File uploaded',
          duration: 2000,
          type: 'primary',
        });
        return response.data.data;
      } else {
        throw new Error('Failed to save file');
      }
    } catch (error: any) {
      console.error('Error saving the edited file:', error);
      toast.show({
        title: 'Operation Failed',
        content: 'Could not upload file',
        type: 'warning',
        duration: 2000,
      });
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState: TrainingStateType = {
  data: {
    data: null,
    pagination: defaultPagination,
  },
  loading: false,
  error: null,
};

const trainingFilesReducer = createSlice({
  name: 'trainingFiles',
  initialState,
  reducers: {
    updateState: (state, action: PayloadAction<FileResponseType[]>) => {
      state.data.data = action.payload;
    },

    updatePagination: (state, action) => {
      state.data.pagination = {
        ...state.data.pagination,
        ...action.payload,
      };
    },

    replaceFileByIntent: (state, action: PayloadAction<FileResponseType>) => {
      const newFile = action.payload;
      if (!state.data.data) {
        state.data.data = [newFile];
        return;
      }
      const existingIndex = state.data.data.findIndex(
        (file) => file.intent === newFile.intent
      );
      if (existingIndex !== -1) {
        state.data.data[existingIndex] = newFile;
      } else {
        state.data.data.unshift(newFile);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrainingFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainingFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchTrainingFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteTrainingFile.fulfilled, (state, action) => {
        const fileId = action.payload?._id;
        if (state.data.data) {
          state.data.data = state.data.data.filter(
            (file) => file._id !== fileId
          );
        }
      })
      .addCase(saveTrainingFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTrainingFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(saveTrainingFile.fulfilled, (state, action) => {
        const newFile = action.payload;
        if (!newFile || !state.data.data) return;
        state.data.data = [
          newFile,
          ...state.data.data.filter((file) => file.intent !== newFile.intent),
        ];
        state.loading = false;
      });
  },
});

export const { updateState, updatePagination, replaceFileByIntent } =
  trainingFilesReducer.actions;

export default trainingFilesReducer.reducer;
